import { Mutex } from "async-mutex";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { sendLoggingEvents, TrackingEvents } from "../events";
import type {
  RemoteAudioDelegator,
  RemoteMediaDelegator,
  RemoteParticipant,
  RemoteParticipantEventMap,
  RemoteParticipantMediaEventHandler,
} from "../video-sdk";
import {
  MediaSource,
  remoteMediaByParticipantIdAtom,
  remoteParticipantToDiagnosticInfo,
  remoteScreenShareAudioDelegatorAtom,
  remoteScreenShareParticipantAtom,
} from "../video-sdk";
import {
  addMediaToParticipant,
  remoteMediaDelagatorToDiagnosticInfo,
  remoteMediaStatusByParticipantIdAtom,
  removeMediaFromParticipant,
} from "../video-sdk/twilio";
import { useShowRestrictedAudioPlaybackToastCallback } from "./audio-playback-restriction-fix";
import { useRemoteMediaEventListeners } from "./useRemoteMediaEventListeners";
import { gracefullyHandleMediaEvents } from "./utils";
import { currentOutputDeviceIdAtom } from "../video-sdk/core/atoms";

export type UseRemoteAudioProps = {
  remoteParticipant: RemoteParticipant;
  onAudioSubscribed?: RemoteParticipantMediaEventHandler;
  onAudioUnsubscribed?: RemoteParticipantMediaEventHandler;
};

const remoteAudioEventMutex = new Mutex();

export type PrimaryEvents = Pick<
  RemoteParticipantEventMap,
  "mediaSubscribed" | "mediaUnsubscribed" | "mediaEnabled"
>;

function useGetRemoteAudioEventListeners({
  getAudioElement,
  remoteParticipant,
  onAudioSubscribed,
  onAudioUnsubscribed,
}: UseRemoteAudioProps & {
  getAudioElement: () => HTMLAudioElement | null;
}) {
  const setRemoteScreenShareParticipant = useSetAtom(
    remoteScreenShareParticipantAtom,
  );
  const setRemoteScreensShareAudioTrack = useSetAtom(
    remoteScreenShareAudioDelegatorAtom,
  );
  const { detectRestrictedAudioPlaybackTimeout } =
    useShowRestrictedAudioPlaybackToastCallback();
  const setRemoteAudioState = useSetAtom(remoteMediaStatusByParticipantIdAtom);
  const setRemoteMediaDelegators = useSetAtom(remoteMediaByParticipantIdAtom);

  return useMemo(
    () => {
      if (!remoteParticipant) return;
      return gracefullyHandleMediaEvents(remoteAudioEventMutex, {
        mediaSubscribed: (remoteMediaDelegator, ...otherArgs) => {
          const element = getAudioElement();

          if (!isRemoteAudioDelegator(remoteMediaDelegator)) return;

          const source = remoteMediaDelegator.source;
          if (source === MediaSource.Screen && remoteParticipant) {
            setRemoteScreensShareAudioTrack(remoteMediaDelegator);
            setRemoteScreenShareParticipant(remoteParticipant);
          }
          if (source === MediaSource.Microphone) {
            if (element) {
              remoteMediaDelegator.attach(element);
              detectRestrictedAudioPlaybackTimeout(
                remoteMediaDelegator,
                element,
                300,
              );
            }
            onAudioSubscribed?.(remoteMediaDelegator, ...otherArgs);
            setRemoteAudioState((prev) => ({
              ...prev,
              [remoteParticipant.identity]: {
                ...prev[remoteParticipant.identity],
                isAudioMuted: !remoteMediaDelegator.isEnabled,
              },
            }));
          }

          setRemoteMediaDelegators((prev) =>
            addMediaToParticipant(
              prev,
              remoteParticipant.identity,
              remoteMediaDelegator,
            ),
          );

          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_SUBSCRIBED,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteMediaDelegator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: "info",
              message: "Remote participant audio subscribed",
            },
          );
        },
        mediaEnabled: (remoteMediaDelegator) => {
          if (!isRemoteAudioDelegator(remoteMediaDelegator)) return;
          setRemoteAudioState((prev) => ({
            ...prev,
            [remoteParticipant.identity]: {
              ...prev[remoteParticipant.identity],
              isAudioMuted: false,
            },
          }));
        },
        mediaUnsubscribed: (remoteMediaDelegator, ...otherArgs) => {
          const element = getAudioElement();
          if (!isRemoteAudioDelegator(remoteMediaDelegator)) return;
          const source = remoteMediaDelegator.source;
          if (source === MediaSource.Screen && remoteParticipant) {
            setRemoteScreenShareParticipant(undefined);
            setRemoteScreensShareAudioTrack(undefined);
          }
          if (source === MediaSource.Microphone) {
            if (element) {
              remoteMediaDelegator.detach(element);
            }
            onAudioUnsubscribed?.(remoteMediaDelegator, ...otherArgs);
            setRemoteAudioState((prev) => ({
              ...prev,
              [remoteParticipant.identity]: {
                ...prev[remoteParticipant.identity],
                isAudioMuted: true,
              },
            }));
          }

          setRemoteMediaDelegators((prev) =>
            removeMediaFromParticipant(
              prev,
              remoteParticipant.identity,
              remoteMediaDelegator,
            ),
          );

          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_UNSUBSCRIBED,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteMediaDelegator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: "info",
              message: "Remote participant audio unsubscribed",
            },
          );
        },
      });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getAudioElement,
      onAudioSubscribed,
      onAudioUnsubscribed,
      remoteParticipant,
    ],
  );
}

export function useRemoteAudio({
  remoteParticipant,
  ...otherProps
}: UseRemoteAudioProps) {
  const remoteParticipantAudioRef = useRef<HTMLAudioElement>(null);
  const getAudioElement = useCallback(
    () => remoteParticipantAudioRef.current,
    [],
  );
  const currentOutputDeviceId = useAtomValue(currentOutputDeviceIdAtom);
  const remoteAudioEventListeners = useGetRemoteAudioEventListeners({
    ...otherProps,
    remoteParticipant,
    getAudioElement,
  });

  useRemoteMediaEventListeners({
    mediaKind: "audio",
    remoteParticipant,
    remoteMediaEventListeners: remoteAudioEventListeners,
  });

  useEffect(
    function useDesignatedOutputDevice() {
      if (!remoteParticipantAudioRef.current || !currentOutputDeviceId) return;
      //@ts-ignore setSinkId is technically experimental
      remoteParticipantAudioRef.current?.setSinkId?.(currentOutputDeviceId);
    },
    [remoteParticipantAudioRef, currentOutputDeviceId],
  );

  return remoteParticipantAudioRef;
}

// type guard
function isRemoteAudioDelegator(
  delegator: RemoteMediaDelegator,
): delegator is RemoteAudioDelegator {
  return delegator.kind === "audio";
}
